import { Box, Group, Pagination, SegmentedControl, Stack, Title } from "@mantine/core";
import type { JSX } from "react";
import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "urql";
import Loading from "../Loading";
import CreateThreadForm from "./CreateThreadForm";
import ImageThreadCard from "./ImageThreadCard";
import classes from "./ImageThreadsPage.module.css";
import { type ImageThreadPageQuery, type ImageThreadPageQueryVariables, imageThreadPageQuery } from "./queries";

function ImageThreadsPage(): JSX.Element {
    const [search, setSearch] = useSearchParams();

    const page = useMemo(() => Number(search.get("page") || 1), [search]);
    const filter = (search.get("filter") as ImageThreadPageQueryVariables["filter"]) || "MINE";

    const [result, refetch] = useQuery<ImageThreadPageQuery, ImageThreadPageQueryVariables>({
        query: imageThreadPageQuery,
        variables: { page, filter },
        requestPolicy: "cache-and-network",
    });

    // biome-ignore lint/correctness/useExhaustiveDependencies: Run only on mount
    useEffect(() => {
        if (!search.get("filter")) {
            setSearch((prev) => {
                const s = new URLSearchParams(prev);
                s.set("filter", filter);
                s.set("page", String(page));
                return s;
            });
        }
    }, []);

    let main: JSX.Element;
    if (result.fetching) {
        main = <Loading />;
    } else if (result.error) {
        main = <Box>Error: {result.error.message}</Box>;
    } else if (!result.data || result.data.imageThreadPage.maxPage === 0) {
        main = <Box>No image threads</Box>;
    } else {
        main = (
            <Stack>
                <Pagination
                    total={result.data.imageThreadPage.maxPage}
                    value={page}
                    onChange={(p) => setSearch({ filter, page: String(p) })}
                />
                <Box className={classes.grid}>
                    {result.data.imageThreadPage.items.map((t) => (
                        <ImageThreadCard key={t.id} id={t.id} latestImageIteration={t.latestImageIteration} />
                    ))}
                </Box>
                <Pagination
                    total={result.data.imageThreadPage.maxPage}
                    value={page}
                    onChange={(p) => setSearch({ filter, page: String(p) })}
                />
            </Stack>
        );
    }

    return (
        <Stack maw={1000} mx="auto" gap="xl">
            <Stack>
                <Title order={1}>Generate Images</Title>
                <CreateThreadForm refetch={refetch} />
            </Stack>

            <Stack>
                <Group justify="space-between">
                    <Title order={2}>Previous Images</Title>
                    <SegmentedControl
                        value={filter}
                        onChange={(f) => setSearch({ filter: f, page: "1" })}
                        data={[
                            { label: "Mine", value: "MINE" },
                            { label: "Others", value: "OTHERS" },
                            { label: "All", value: "ALL" },
                        ]}
                    />
                </Group>
                {main}
            </Stack>
        </Stack>
    );
}

export default ImageThreadsPage;
